import { useState, useEffect, useMemo } from 'react'
import { PurchaseRequest } from '../types'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import {
  loadPurchaseRequests,
  approvePurchaseRequestByLeader,
  rejectPurchaseRequestByLeader,
  cancelPurchaseRequestByLeader,
} from '../lib/api'
import PurchaseRequestsTable from '../components/PurchaseRequestsTable'
import PurchaseRequestCard from '../components/PurchaseRequestCard'
import Pagination from '../components/Pagination'
import { CancelRequestModal } from '../components/CancelRequestModal'
import { useIsMobile } from '../hooks/useMediaQuery'
import {
  getPurchaseStatusLabel,
  getPurchaseStatusClass,
  LEADER_CANCEL_STATUSES,
} from '../lib/purchaseRequestStatus'
import './VedouciView.css'

interface PurchaseLeaderViewProps {
  purchaseRole?: string
}

function PurchaseLeaderView({ purchaseRole = 'vedouci' }: PurchaseLeaderViewProps) {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const [requests, setRequests] = useState<PurchaseRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [statusFilter, setStatusFilter] = useState<string | null>('pending_leader')
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rejectModal, setRejectModal] = useState<PurchaseRequest | null>(null)
  const [cancelModal, setCancelModal] = useState<PurchaseRequest | null>(null)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    if (user) loadRequestsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, purchaseRole])

  const loadRequestsData = async () => {
    if (!user) return
    try {
      setLoading(true)
      const data = await loadPurchaseRequests(user.id, user.role ?? null, purchaseRole || 'vedouci')
      setRequests(data)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí o nákup')
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: !loading,
    onFound: (r) => {
      setStatusFilter(r.status)
      setSearchText(r.subject || '')
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již byla zpracována.', 'error'),
    elementIdFor: (id) => `purchase-${id}`,
  })

  const requestsForStats = useMemo(() => {
    if (!searchText.trim()) return requests
    const searchLower = searchText.toLowerCase().trim()
    return requests.filter((r) => {
      const hay = [
        r.subject,
        r.description,
        r.reason,
        r.supplier,
        r.center_name,
        r.created_by_name,
        r.notes,
      ]
        .join(' ')
        .toLowerCase()
      return hay.includes(searchLower)
    })
  }, [requests, searchText])

  const filteredRequests = useMemo(() => {
    if (!statusFilter) return requestsForStats
    return requestsForStats.filter((r) => r.status === statusFilter)
  }, [requestsForStats, statusFilter])

  const pending = useMemo(
    () => requestsForStats.filter((r) => r.status === 'pending_leader'),
    [requestsForStats]
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, searchText])

  const pageItems = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / itemsPerPage))

  const handleApprove = async (request: PurchaseRequest) => {
    if (!confirm('Schválit tuto žádost o nákup?')) return
    try {
      await approvePurchaseRequestByLeader(request.id, purchaseRole)
      await loadRequestsData()
    } catch (e: any) {
      alert(e?.message || 'Chyba při schválení')
    }
  }

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectModal) return
    try {
      await rejectPurchaseRequestByLeader(rejectModal.id, reason, purchaseRole)
      setRejectModal(null)
      await loadRequestsData()
    } catch (e: any) {
      alert(e?.message || 'Chyba při zamítnutí')
    }
  }

  const handleCancelConfirm = async (reason: string) => {
    if (!cancelModal) return
    try {
      await cancelPurchaseRequestByLeader(cancelModal.id, reason, purchaseRole)
      setCancelModal(null)
      await loadRequestsData()
    } catch (e: any) {
      alert(e?.message || 'Chyba při zrušení')
    }
  }

  const leaderActions = (request: PurchaseRequest) => {
    if (request.status === 'pending_leader') {
      return (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn-approve-small" onClick={() => handleApprove(request)}>
            Schválit
          </button>
          <button className="btn-reject-small" onClick={() => setRejectModal(request)}>
            Zamítnout
          </button>
        </div>
      )
    }
    if ((LEADER_CANCEL_STATUSES as readonly string[]).includes(request.status)) {
      return (
        <button className="btn-reject-small" onClick={() => setCancelModal(request)}>
          Zrušit žádost
        </button>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div className="vedouci-view">
        <div className="loading">Načítání...</div>
      </div>
    )
  }

  return (
    <div className="vedouci-view">
      <div className="vedouci-header">
        <p style={{ margin: 0, color: '#64748b' }}>
          Schvalujte žádosti, kde jste zvoleni jako vedoucí.
        </p>
      </div>

      <div
        className="view-controls"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}
      >
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')}>
            Karty
          </button>
          <button className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}>
            Tabulka
          </button>
        </div>
        <input
          type="text"
          placeholder="Hledat podle textu..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', minWidth: '250px' }}
        />
      </div>

      <div className="admin-stats">
        <div
          className={`stat-card ${statusFilter === 'pending_leader' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'pending_leader' ? null : 'pending_leader')}
          style={{ cursor: 'pointer' }}
        >
          <h3>Ke schválení</h3>
          <p className="stat-number">{pending.length}</p>
        </div>
        <div
          className={`stat-card ${statusFilter === null ? 'active' : ''}`}
          onClick={() => setStatusFilter(null)}
          style={{ cursor: 'pointer' }}
        >
          <h3>Všechny</h3>
          <p className="stat-number">{requestsForStats.length}</p>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <div className="requests-list">
          {pageItems.length === 0 ? (
            <p className="empty-state">Žádné žádosti</p>
          ) : (
            pageItems.map((request) => (
              <PurchaseRequestCard
                key={request.id}
                request={request}
                showActions
                getStatusLabel={getPurchaseStatusLabel}
                getStatusClass={getPurchaseStatusClass}
                customActions={leaderActions(request)}
                searchText={searchText}
                onAction={() => loadRequestsData()}
              />
            ))
          )}
        </div>
      ) : (
        <PurchaseRequestsTable
          requests={pageItems}
          filterType="vedouci"
        />
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredRequests.length}
        />
      )}

      {rejectModal && (
        <CancelRequestModal
          isOpen
          title="Zamítnout žádost"
          warning="Opravdu chcete zamítnout tuto žádost? Žadatel dostane e-mail s důvodem."
          reasonLabel="Důvod zamítnutí"
          confirmLabel="Zamítnout"
          requestSubject={rejectModal.subject}
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectModal(null)}
        />
      )}
      {cancelModal && (
        <CancelRequestModal
          isOpen
          title="Zrušit žádost"
          warning="Opravdu chcete zrušit tuto žádost jako vedoucí?"
          reasonLabel="Důvod zrušení"
          confirmLabel="Zrušit"
          requestSubject={cancelModal.subject}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelModal(null)}
        />
      )}
    </div>
  )
}

export default PurchaseLeaderView
